import { useEffect, useState } from "react";
import { Row, Col, Card, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { openTabAndPassData } from "utils/newTab";
import OrchidProAvatar from "components/orchid-pro-avatar";
import {
  getOrchidProCertificationString,
  getOrchidProAbbrevNameAndAllDegreesString,
  getOrchidProTypeStringFromNewArr,
} from "utils/formatter/orchidPro";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import NextButton from "components/pagination-buttons/components/next-button";
import PreviousButton from "components/pagination-buttons/components/previous-button";
import AspectRatio from "components/aspect-ratio";
import { ReactComponent as OutlineArrowRightCircle } from "assets/svg/outline-arrow-right-circle.svg";
import styles from "./styles.module.scss";
import searchOrchidPro from "services/searchOrchidPro";
import { AmplitudePageName, track } from "utils/amplitude";

const getCountPerPage = (deviceWidth) => {
  return deviceWidth >= 1920
    ? 5
    : deviceWidth >= 1440
    ? 4
    : deviceWidth >= 992
    ? 3
    : 2;
};

const ANALYTICS_COMPONENT_NAME = "grow-your-network-section";

interface Props {
  amplitudePageName: AmplitudePageName;
}

const GrowYourNetwork: React.FC<Props> = ({ amplitudePageName }) => {
  const { isPhonePortrait } = usePhonePortraitContext();
  const [{ orchidPros, totalCount }, setSearchOrchidProResult] = useState<{
    orchidPros: OrchidPro[];
    totalCount: number;
  }>({
    orchidPros: [],
    totalCount: 0,
  });
  const [countPerPage, setCountPerPage] = useState(
    getCountPerPage(window.innerWidth),
  );
  const [page, setPage] = useState(1);

  const updateDimensions = () => {
    setCountPerPage(getCountPerPage(window.innerWidth));
  };
  const getOrchidPro = async (nextPage: number) => {
    try {
      const res = await searchOrchidPro({
        limit: countPerPage,
        skip: (nextPage - 1) * countPerPage,
      });
      setSearchOrchidProResult({
        orchidPros: res.results,
        totalCount: res.total_count,
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getOrchidPro(page);
    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
    // on init
    // eslint-disable-next-line
  }, []);

  const nextPage = (page) => {
    const nextPage = page + 1;
    setPage(nextPage);
    getOrchidPro(nextPage);
  };

  const prevPage = (page) => {
    const nextPage = page - 1;
    setPage(nextPage);
    getOrchidPro(nextPage);
  };

  const pageMax = Math.ceil(totalCount / countPerPage);

  return (
    <>
      <Row className="justify-content-between">
        <div>
          <div className="deep-blue bd-18">Grow your network</div>
          <div className="gray01">Connect with other professionals</div>
        </div>
        <Link
          to="/professional-search"
          onClick={() =>
            track(
              amplitudePageName,
              [ANALYTICS_COMPONENT_NAME, "view-all-professionals-link"],
              "click",
            )
          }
        >
          <div className="d-flex align-items-center md-18">
            View all professionals
            <OutlineArrowRightCircle className="ml-2" />
          </div>
        </Link>
      </Row>

      <Row className="justify-content-end">
        <PreviousButton
          disabled={page <= 1}
          onClick={() => {
            track(
              amplitudePageName,
              [ANALYTICS_COMPONENT_NAME, "prev-button"],
              "click",
            );
            prevPage(page);
          }}
        />
        <NextButton
          disabled={page >= pageMax}
          onClick={() => {
            track(
              amplitudePageName,
              [ANALYTICS_COMPONENT_NAME, "next-button"],
              "click",
            );
            nextPage(page);
          }}
        />
      </Row>
      <Row className={styles["group-your-network-card-list"]}>
        {orchidPros.slice(0, countPerPage).map((orchidPro) => {
          return (
            <Col
              className={
                !isPhonePortrait
                  ? styles["group-your-network-card"]
                  : "p-0 mb-3"
              }
              key={orchidPro.cognito_sub}
              style={{
                flex: !isPhonePortrait ? `0 0 ${1 / countPerPage}` : "auto",
              }}
            >
              <AspectRatio
                ratio={1.57}
                wrapperClassName={styles["group-your-network-card__wrapper"]}
                contentClassName={styles["group-your-network-card__content"]}
              >
                <Card className="w-100 h-100 rounded-lg">
                  <Link
                    className="w-100 h-100"
                    to={"/professional-search/" + orchidPro.custom_url}
                    onClick={() =>
                      track(
                        amplitudePageName,
                        [ANALYTICS_COMPONENT_NAME, "pro-card"],
                        "click",
                      )
                    }
                  >
                    <Card.Body
                      className={styles["group-your-network-card__body"]}
                    >
                      <OrchidProAvatar
                        orchidPro={orchidPro}
                        style={{
                          width: "3.75rem",
                          height: "3.75rem",
                        }}
                      />
                      <span
                        className={
                          styles["group-your-network-card__body-right"]
                        }
                      >
                        <div className={"deep-blue md-16 module line-clamp-1"}>
                          {getOrchidProAbbrevNameAndAllDegreesString(orchidPro)}
                        </div>
                        {!orchidPro.orchid_pro_types?.length ? null : (
                          <p
                            className={"mb-0 rg-14 darker module line-clamp-2"}
                          >
                            {getOrchidProTypeStringFromNewArr(orchidPro)}
                          </p>
                        )}
                        {!orchidPro.orchid_pro_types?.length ? null : (
                          <p className="rg-12 gray01 module line-clamp-1">
                            {getOrchidProCertificationString(orchidPro)}
                          </p>
                        )}
                      </span>
                    </Card.Body>
                  </Link>
                  <Card.Footer className="justify-content-end d-flex pt-0 background-transparent border-0">
                    <Button
                      variant="outline-primary"
                      onClick={() => {
                        track(
                          amplitudePageName,
                          [ANALYTICS_COMPONENT_NAME, "send-message-button"],
                          "click",
                        );
                        openTabAndPassData(
                          `/pro-dashboard/messages/${orchidPro.cognito_sub}`,
                          { activeContactsTab: "professional" },
                        );
                      }}
                    >
                      Send message
                    </Button>
                  </Card.Footer>
                </Card>
              </AspectRatio>
            </Col>
          );
        })}
        {countPerPage - orchidPros.length !== countPerPage &&
          Array.from({ length: countPerPage - orchidPros.length }).map(
            (_, i) => {
              return (
                <Col
                  key={i}
                  className={styles["group-your-network-card"]}
                  style={{ flex: `0 0 ${1 / countPerPage}` }}
                ></Col>
              );
            },
          )}
      </Row>
    </>
  );
};

export default GrowYourNetwork;
